import React from "react";
import {
    Box,
    Typography,
    Modal,
    Button,
    Paper,
    IconButton,
    Grid,
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";

// Styled floating modal container
const FloatingWindow = styled(Box)({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "50%", // Reduced width
    backgroundColor: "#121212",
    color: "#FFFFFF",
    border: "2px solid #00FFFF",
    borderRadius: "12px",
    boxShadow: "0 0 30px rgba(0, 255, 255, 0.3)",
    padding: "20px",
    maxHeight: "80vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
});

// Header with FIG logo
const Header = styled(Box)({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
});

// Multiline content box (for Prompt/Response)
const ContentBox = styled(Paper)({
    backgroundColor: "#1A1A1A",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #00FFFF",
    boxShadow: "0 0 10px #00FFFF",
    marginBottom: "20px",
    maxHeight: "300px",
    overflowY: "auto",
    color: "#FFFFFF",
});

// Simple styled field label + value container
const FieldItem = ({ label, value }) => (
    <Box sx={{ marginBottom: 2 }}>
        <Typography variant="subtitle2" sx={{ color: "#00FFFF", fontWeight: "bold" }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {value ?? "N/A"}
        </Typography>
    </Box>
);

const ChatDetailsModal = ({ open, onClose, rowData }) => {
    const handlePrint = () => {
        const printContents = document.getElementById("print-content").innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
    };

    if (!rowData) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <FloatingWindow id="print-content">
                {/* Header with FIG logo */}
                <Header>
                    <Box
                        component="img"
                        src="https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png"
                        alt="FIG Logo"
                        sx={{
                            width: "80px",
                            height: "80px",
                            objectFit: "contain",
                            border: "2px solid #00FFFF",
                            borderRadius: "12px",
                            boxShadow: "0 0 10px #00FFFF",
                        }}
                    />
                    <IconButton onClick={onClose} sx={{ color: "#FFFFFF" }}>
                        <CloseIcon />
                    </IconButton>
                </Header>

                {/* 3-Column Grid for the smaller fields */}
                <Grid container spacing={2} sx={{ marginBottom: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <FieldItem
                            label="Log ID"
                            value={rowData.AI_PromptResponseLogID}
                        />
                        <FieldItem label="API ID" value={rowData.apiid} />
                        <FieldItem label="Source Page ID" value={rowData.SourcePageID} />
                        <FieldItem label="Source Page URL" value={rowData.SourcePageURL} />
                        <FieldItem label="Storage Location" value={rowData.StorageLocation} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldItem label="User ID" value={rowData.UserID} />
                        <FieldItem label="Organization ID" value={rowData.OrganizationID} />
                        <FieldItem label="Bot ID" value={rowData.BotID} />
                        <FieldItem label="IP Address" value={rowData.ipaddress} />
                        <FieldItem
                            label="Analyzed?"
                            value={rowData.IsAnalyzed ? "Yes" : "No"}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FieldItem label="Main Category" value={rowData.MainCategory} />
                        <FieldItem label="Sub Category" value={rowData.SubCategory} />
                        <FieldItem label="Token Amount" value={rowData.TokenAmount} />
                        <FieldItem
                            label="Prompt Date"
                            value={rowData.PromptDate ?? "N/A"}
                        />
                        <FieldItem
                            label="Response Date"
                            value={rowData.ResponseDate ?? "N/A"}
                        />
                    </Grid>
                </Grid>

                {/* Prompt Section */}
                <Typography
                    variant="h5"
                    sx={{ marginBottom: "10px", color: "#00FFFF", fontWeight: "bold" }}
                >
                    Prompt
                </Typography>
                <ContentBox>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                        {rowData.PromptText || "No Prompt Available"}
                    </Typography>
                </ContentBox>

                {/* Response Section */}
                <Typography
                    variant="h5"
                    sx={{ marginBottom: "10px", color: "#00FFFF", fontWeight: "bold" }}
                >
                    Response
                </Typography>
                <ContentBox>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                        {rowData.ResponseText || "No Response Available"}
                    </Typography>
                </ContentBox>

                {/* Print Button */}
                <Button
                    variant="contained"
                    onClick={handlePrint}
                    sx={{
                        alignSelf: "flex-end",
                        backgroundColor: "#00FFFF",
                        color: "#121212",
                        "&:hover": { backgroundColor: "#00CED1" },
                    }}
                >
                    Print
                </Button>
            </FloatingWindow>
        </Modal>
    );
};

export default ChatDetailsModal;
